import { useState, useEffect, useCallback } from 'react';
import {
  faUser,
  faUserFriends,
  faUpload,
  faBars,
  faAnglesLeft,
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import MatterService from '../../../../../../AppSettings/services/MatterService';
import ExportModal from '../../../../../../Components/Export/ExportModal';
import ReportingService from '../../../../../../AppSettings/services/ReportingService';
import { isLegalEnvironment } from '../../../../../../AppSettings/environmentConfig';
import { detailRouteBase } from '../../../../../utils/detailRoute';
import UnifiedTopBar from '@frontend/Components/TopBar';

const EXPORT_MODAL_ID = 'matter-tasks-export';
const EXPORT_FILENAME_PREFIX = 'TasksByMatter';
const EXPORT_COLUMNS = [
  'Task ID',
  'Matter ID',
  'Task Title',
  'Description',
  'Due Date',
  'Assigned To',
  'Status',
  'Created By',
  'Task Type',
];

interface TopBarProps {
  onSearch: (searchTerm: string, column?: string | null) => void;
  onToggleTasks: (value: any) => void;
  activeTable: any;
  columns: any;
  permishId: any;
}

const TopBar = ({ onSearch, onToggleTasks, activeTable, columns, permishId }: TopBarProps) => {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [matterName, setMatterName] = useState<{ mattername: string; sept_tenantid: string; } | null>(null);
  const [currentTasks, setCurrentTasks] = useState(true); // State to track current tasks
  const isLegalEnv = isLegalEnvironment;

  const fetchMatterName = useCallback(async () => {
    try {
      const response = await MatterService.getMatterName(matterId!);
      setMatterName(response ?? null);
    } catch (error) {
      console.error('Error fetching matter name:', error);
      setMatterName(null);
    }
  }, [matterId]);

  useEffect(() => {
    fetchMatterName();
  }, [matterId, fetchMatterName]);

  // Custom data processor for tasks by matter export
  const customTasksDataProcessor = useCallback(async (data: any, selectedColumns: any) => {
    try {
      const response = await ReportingService.getTasksByMatterExcel(
        matterId!,
        selectedColumns
      );

      const blob = response as Blob;
      // if (response.status !== 200) {
      //   throw new Error(`Failed to download report: ${response.statusText}`);
      // }

      // // Create blob from the binary response data
      // let blob;
      // if (response.data instanceof Blob) {
      //   // If response is already a blob, use it directly
      //   blob = response.data;
      // } else {
      //   // Otherwise, create a new blob
      //   blob = new Blob([response.data], {
      //     type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      //   });
      // }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `TasksByMatter_${matterId}_Report.xlsx`;
      a.style.display = 'none'; // Hide the element

      // Add to DOM, click, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 1000); // Increased timeout for better reliability

      // Return empty array since we handled the download manually
      return [];
    } catch (error) {
      console.error('Error downloading tasks report:', error);
      throw error;
    }
  }, [matterId]);

  const handleToggle = useCallback(
    (bool: any) => {
      setCurrentTasks(bool); // Update local state
      onToggleTasks(bool); // Call the external function
    },
    [onToggleTasks]
  );

  // Convert permishId to number for comparison
  const permissionId = permishId != null ? Number(permishId) : null;
  
  // Debug logging
  console.log('MatterTasks TopBar permishId:', permishId, 'permissionId:', permissionId);

  const exportModalComponent = useCallback(
    ({ isOpen, onClose }: { isOpen: any; onClose: any }) => (
      <ExportModal
        isOpen={isOpen}
        onClose={onClose}
        data={[]}
        columns={EXPORT_COLUMNS as any}
        filename={`${EXPORT_FILENAME_PREFIX}_${matterId}_Report`}
        title="Export Tasks by Matter"
        customDataProcessor={customTasksDataProcessor as any}
      />
    ),
    [customTasksDataProcessor, matterId]
  );

  const matterTitle = matterName?.mattername
    ? `${matterId} ${matterName.mattername}`
    : 'Loading...';

  const leftElements = [
    {
      type: 'button',
      icon: faAnglesLeft,
      onClick: () => navigate(`${detailRouteBase()}/${matterId}`),
      ariaLabel: 'Back to matter details',
    },
    {
      type: 'text',
      text: matterTitle,
      style: { marginLeft: '10px', fontWeight: 'bold', color: '#545465' },
    },
  ] as any;

  if (activeTable === 'Tasks') {
    const toggleOptions = [
      {
        icon: faUser,
        value: true,
        ariaLabel: 'Show my tasks',
      },
    ];

    if (permissionId === 1 || permissionId === 2) {
      toggleOptions.push({
        icon: faUserFriends,
        value: false,
        ariaLabel: 'Show all tasks',
      });
    }

    leftElements.push({
      type: 'toggle',
      options: toggleOptions,
      value: currentTasks,
      onChange: handleToggle,
    });
  }

  const dropdownItems = [
    { label: 'General', onClick: () => navigate(`${detailRouteBase()}/${matterId}`) },
    { label: 'Tasks', onClick: () => navigate(`${detailRouteBase()}/${matterId}/tasks`) },
    // Legal only: `/matterDetails/:matterId/financial/*` is registered inside App.js's
    // isLegalEnvironment block and has no engineering counterpart — engineering money lives on
    // the pipeline Commercial tab and /financials/*. detailRouteBase() resolves to
    // /projectDetails there, and with no catch-all route the tab rendered a blank page. The
    // retired project top bars carried four items and no Financial; the consolidation onto
    // these shared components is what surfaced it in the wrong vertical.
    ...(isLegalEnvironment
      ? [{ label: 'Financial', onClick: () => navigate(`${detailRouteBase()}/${matterId}/financial`) }]
      : []),
    { label: 'Files', onClick: () => navigate(`${detailRouteBase()}/${matterId}/files`) },
    { label: 'Notes', onClick: () => navigate(`${detailRouteBase()}/${matterId}/additionalNotes`) },
  ];

  const hasTaskColumn = columns && Object.prototype.hasOwnProperty.call(columns, 'Task');
  const defaultSearchColumn = hasTaskColumn
    ? 'Task'
    : Object.keys(columns || {})[0] || 'Task';

  const rightElements = [] as any;

  if (isLegalEnv) {
    rightElements.push({
      type: 'modalTrigger',
      icon: faUpload,
      ariaLabel: 'Export tasks by matter',
      modalId: EXPORT_MODAL_ID,
    });
  }

  rightElements.push({
    type: 'search',
    onSearch,
    columns,
    defaultSelectedColumn: defaultSearchColumn,
  });

  rightElements.push({
    type: 'dropdown',
    trigger: {
      icon: faBars,
      ariaLabel: 'Open matter navigation menu',
    },
    items: dropdownItems,
  });

  const modals = isLegalEnv
    ? [
        {
          id: EXPORT_MODAL_ID,
          type: 'custom' as any,
          component: exportModalComponent,
        },
      ]
    : [];

  return (
    <UnifiedTopBar
      leftElements={leftElements}
      rightElements={rightElements}
      modals={modals}
      // className="MatterDetailsTopBar"
      // TODO: Readd?
      pageContext={{
        route: 'matter-tasks',
        matterId,
        permissionId,
        activeTable,
      }}
    />
  );
};

export default TopBar;
